import React, { useState, useRef } from 'react';
import service from '../../service';
import { cn } from '@Utils/cn';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import type { MessageChannel } from '@Components/InboxChannelSelector';
import type { Recipients } from '@Components/RecipientSelector';
import ShowFor from '@Components/ShowFor';
import QuickMessagePicker from '@Components/QuickMessagePicker';
import { focusTextareaAt, insertAtCursor } from '@Utils/insertAtCursor';
import type { QuickMessage } from '@Types/messageTemplate';

interface MessageSendBoxProps {
  channel: MessageChannel;
  recipients: Recipients;
  fromInboxId: string;
  /** Existing email thread to reply into, rather than opening a new one. */
  threadId?: string;
  /** Subject of the thread being replied to. Locks the subject field. */
  subject?: string;
  onAfterSend?: () => void;
  className?: string;
}

export function MessageSendBox({
  channel,
  recipients,
  fromInboxId,
  threadId,
  subject,
  onAfterSend,
  className,
}: MessageSendBoxProps) {
  const [messageBody, setMessageBody] = useState('');
  const [messageSubject, setMessageSubject] = useState('');
  const [isSending, setIsSending] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const isBulkMode =
    recipients.mode === 'multiple' || recipients.mode === 'targeting';

  // UUID validation regex
  const isValidUUID = (str: string) => {
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  };

  const hasRecipients = () => {
    if (recipients.mode === 'single') {
      // Must have a singleFish with a valid UUID id
      // console.log(
      //   'recipient',
      //   recipients,
      //   '\nis id valid',
      //   isValidUUID(recipients.singleFish?.id),
      // );
      return (
        !!recipients.singleFish?.id && isValidUUID(recipients.singleFish?.id)
      );
    }
    if (recipients.mode === 'multiple') {
      // Must have at least one valid fish ID
      return (
        recipients.fishIds &&
        recipients.fishIds.length > 0 &&
        recipients.fishIds.every((id) => isValidUUID(id))
      );
    }
    if (recipients.mode === 'targeting') {
      return (
        (recipients.segmentIds && recipients.segmentIds.length > 0) ||
        (recipients.tagIds && recipients.tagIds.length > 0)
      );
    }
    return false;
  };

  const canSend = messageBody.trim() && hasRecipients() && !isSending;

  const handleSend = async () => {
    const trimmedBody = messageBody.trim();
    if (!trimmedBody || !hasRecipients()) return;

    setIsSending(true);

    try {
      if (recipients.mode === 'single' && recipients.singleFish) {
        // Single recipient messaging
        const result = await service.sendMessage({
          fromInboxId,
          toFishId: recipients.singleFish.id,
          method: channel,
          messageBody: trimmedBody,
          messageSubject:
            channel === 'EMAIL' ? (subject ?? messageSubject) : undefined,
          ...(channel === 'EMAIL' && threadId ? { threadId } : {}),
        });

        if (!result.success) {
          throw new Error(result.error || 'Failed to send message');
        }
      } else {
        // Bulk messaging
        const result = await service.sendBulkMessage({
          fromInboxId,
          recipients: {
            fishIds: recipients.fishIds || [],
            segmentIds: recipients.segmentIds || [],
            tagIds: recipients.tagIds || [],
          },
          method: channel,
          messageBody: trimmedBody,
          messageSubject: channel === 'EMAIL' ? messageSubject : undefined,
          filterSubscribed: true,
        });

        if (!result.success) {
          throw new Error(result.error || 'Failed to send messages');
        }

        const sentCount = result.results?.filter(
          (r: { success: boolean }) => r.success,
        ).length;
        const totalCount = result.results?.length || 0;
        toastSuccess(`Sent ${sentCount}/${totalCount} messages successfully`);
      }

      // Reset form
      setMessageBody('');
      setMessageSubject('');
      if (textAreaRef.current) {
        textAreaRef.current.style.height = '';
      }

      if (onAfterSend) {
        onAfterSend();
      }
    } catch (error) {
      console.error('Send error:', error);
      toastError(
        error instanceof Error ? error.message : 'Failed to send message',
      );
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && canSend) {
      e.preventDefault();
      handleSend();
    }
  };

  const getPlaceholder = () => {
    if (!hasRecipients()) {
      return 'Select recipient(s) to send a message...';
    }
    switch (channel) {
      case 'SMS':
        return 'Type an SMS message...';
      case 'APP':
        return 'Type an in-app message...';
      case 'EMAIL':
        return 'Type an email message...';
      default:
        return 'Type a message...';
    }
  };

  const getButtonLabel = () => {
    if (isSending) return 'Sending...';
    if (isBulkMode) return 'Send to All';
    return 'Send';
  };

  const handleQuickMessage = (message: QuickMessage) => {
    const next = insertAtCursor(
      textAreaRef.current,
      messageBody,
      message.messageBody,
    );
    setMessageBody(next.value);
    if (
      channel === 'EMAIL' &&
      !subject &&
      !messageSubject.trim() &&
      message.messageSubject
    ) {
      setMessageSubject(message.messageSubject);
    }
    focusTextareaAt(textAreaRef.current, next.caret);
  };

  return (
    <div className={cn('border-t border-gray-300 p-2', className)}>
      {channel === 'EMAIL' &&
        (subject ? (
          <p className="mb-2 truncate border-b border-gray-200 px-2 py-1 text-sm text-gray-500">
            {subject}
          </p>
        ) : (
          <div className="mb-2">
            <input
              type="text"
              placeholder="Subject..."
              value={messageSubject}
              onChange={(e) => setMessageSubject(e.target.value)}
              className="w-full border-b border-gray-200 px-2 py-1 outline-none focus:border-blue-500"
              disabled={!hasRecipients() || isSending}
            />
          </div>
        ))}
      <div className="flex items-end">
        <ShowFor permissions={['MESSAGE_TEMPLATES_READ']}>
          <QuickMessagePicker
            channel={channel}
            canResolveMergeTags
            onSelect={handleQuickMessage}
            disabled={!hasRecipients() || isSending}
          />
        </ShowFor>
        <textarea
          ref={textAreaRef}
          rows={1}
          placeholder={getPlaceholder()}
          className={cn(
            'no-scrollbar max-h-[50vh] min-h-[24px] flex-1 resize-none overflow-y-auto border-none px-2 py-1 outline-none',
            !hasRecipients() && 'cursor-not-allowed bg-gray-50',
          )}
          value={messageBody}
          onChange={(e) => {
            setMessageBody(e.target.value);
            e.target.style.height = 'auto';
            e.target.style.height = e.target.scrollHeight + 'px';
          }}
          onKeyDown={handleKeyDown}
          disabled={!hasRecipients() || isSending}
        />
        <button
          onClick={handleSend}
          disabled={!canSend}
          className={cn(
            'ml-2 rounded px-4 py-2 text-white transition-colors',
            canSend
              ? 'bg-blue-500 hover:bg-blue-600'
              : 'cursor-not-allowed bg-gray-400',
          )}
        >
          {getButtonLabel()}
        </button>
      </div>
      {isBulkMode && hasRecipients() && (
        <p className="mt-1 text-xs text-gray-500">
          Messages will only be sent to subscribed members
        </p>
      )}
    </div>
  );
}

export default MessageSendBox;
